import React from 'react';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt } from 'react-icons/fa';

const projects = [
  {
    name: 'DropWave - Podcast Hosting SaaS',
    industry: 'Media SaaS',
    url: 'https://dropwave.io/',
    screenshot: '',
    technologies: ['Laravel', 'TailwindCSS', 'Redis', 'AWS S3'],
    description:
      'Scalable podcast hosting platform with multi-tenant architecture, media processing workflows, and cloud storage integrations.',
  },
  {
    name: 'GiftDaddy - Subscription Commerce',
    industry: 'E-commerce',
    url: 'https://www.giftdaddy.com/',
    screenshot: '',
    technologies: ['WordPress', 'CodeIgniter', 'MySQL', 'APIs'],
    description:
      'Subscription commerce platform with automated recurring billing, order lifecycle workflows, and integrations for payment operations.',
  },
  {
    name: 'The Forte School - School Management',
    industry: 'Education',
    url: 'https://theforteschool.org/',
    screenshot: '',
    technologies: ['WordPress', 'Custom PHP', 'MySQL'],
    description:
      'School management platform with student workflows, scheduling modules, and administrative reporting capabilities.',
  },
  {
    name: 'Infertility Aide - Healthcare Platform',
    industry: 'Healthcare',
    url: 'https://www.infertilityaide.com/',
    screenshot: '',
    technologies: ['WordPress', 'Core PHP', 'MySQL'],
    description:
      'Healthcare service platform built with secure data handling, patient-oriented workflows, and operational automation support.',
  },
  {
    name: 'Sales Architects - Consulting Platform',
    industry: 'Consulting',
    url: 'https://www.salesarchitects.com/',
    screenshot: '',
    technologies: ['WordPress', 'PHP', 'REST APIs'],
    description:
      'Custom consulting platform with CMS capabilities, client workflows, and API integrations for business process efficiency.',
  },
];

const previewThemes = [
  'from-slate-900 via-slate-800 to-slate-700',
  'from-blue-900 via-indigo-800 to-indigo-700',
  'from-emerald-900 via-teal-800 to-cyan-700',
  'from-zinc-900 via-zinc-800 to-zinc-700',
];

const getDomain = (url) => {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
};

const ProjectsV2 = () => {
  return (
    <section id="projects-v2" className="py-16 md:py-20 lg:py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="w-full rounded-2xl bg-gray-50 border border-gray-200 p-6 md:p-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">Selected Projects</h2>
        <div className="w-16 h-1 bg-blue-600 rounded mt-2 mb-6"></div>
        <p className="text-sm md:text-base text-gray-600 max-w-2xl mb-8">
          Production systems delivered across SaaS, enterprise, and industry-focused platforms.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((project, i) => (
            <motion.article
              key={project.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="rounded-xl bg-white p-6 md:p-8 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 space-y-3"
            >
              <div className="relative rounded-lg overflow-hidden mb-5">
                {project.screenshot ? (
                  <img
                    src={project.screenshot}
                    alt={`${project.name} preview`}
                    className="w-full h-auto rounded-lg"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`w-full h-48 md:h-56 lg:h-64 bg-gradient-to-br ${previewThemes[i % previewThemes.length]} p-4 flex flex-col justify-between rounded-lg`}
                  >
                    <div className="flex items-center justify-between">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${getDomain(project.url)}&sz=128`}
                        alt={`${project.name} favicon`}
                        className="w-9 h-9 rounded-lg bg-white p-1"
                        loading="lazy"
                      />
                      <span className="text-white/80 text-xs font-medium">Live Project</span>
                    </div>
                    <div>
                      <p className="text-white/70 text-[11px] uppercase tracking-wider mb-1">
                        {getDomain(project.url)}
                      </p>
                      <p className="text-white text-sm font-semibold leading-snug">
                        {project.name.split(' - ')[0]}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">{project.industry}</p>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 leading-snug">{project.name}</h3>
              <p className="max-w-prose text-sm md:text-base text-gray-600 leading-relaxed mb-4">{project.description}</p>

              <div className="flex flex-wrap gap-2 mb-5">
                {project.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
              >
                View Project
                <FaExternalLinkAlt className="text-xs" />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
      </div>
    </section>
  );
};

export default ProjectsV2;
